"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Coins, Zap, ChartBar as BarChart3, ShieldCheck, MessageSquare, Check, X, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const comparisonPoints = [{
  id: 1,
  title: "1. Direct Access to Senior Expertise",
  consultant: "You work directly with me, Yassine Essaadi. Every strategy, technical audit, and campaign decision is handled personally by a senior specialist.",
  agency: "Pitched by senior founders, then passed down to junior account executives learning on your dime.",
  accentTag: "100% Senior Level",
  icon: UserCheck
}, {
  id: 2,
  title: "2. Cost Efficiency & Zero Agency Overhead",
  consultant: "Every pound of your budget goes into actual SEO work. You don't fund fancy city offices, account managers, or corporate bloat.",
  agency: "High monthly retainers inflated by agency overheads, account management fees, and non-essential staff.",
  accentTag: "Maximized ROI",
  icon: Coins
}, {
  id: 3,
  title: "3. Fully Tailored & Flexible Strategy",
  consultant: "I craft bespoke SEO blueprints specific to your industry, revenue targets, and current technical setup. No rigid templates.",
  agency: "Standardized cookie-cutter checklists and automated audit exports delivered on fixed monthly schedules.",
  accentTag: "Bespoke Approach",
  icon: Zap
}, {
  id: 4,
  title: "4. Agile Execution & Fast Communication",
  consultant: "Agile, direct communication via Slack, email, or phone. Decisions and implementations happen in days, not weeks.",
  agency: "Bureaucratic sign-off chains, formal ticket systems, and slow turnarounds through middle management.",
  accentTag: "Rapid Delivery",
  icon: MessageSquare
}, {
  id: 5,
  title: "5. Transparent Reporting & Real Business Impact",
  consultant: "Clear, plain-English reporting focused strictly on organic traffic, qualified leads, and measurable revenue growth.",
  agency: "Confusing quarterly slide decks filled with vanity rankings and vague metrics to justify retainers.",
  accentTag: "Honest Metrics",
  icon: BarChart3
}, {
  id: 6,
  title: "6. true Ownership & Direct Accountability",
  consultant: "I personally take ownership of your campaign results. Your organic search success directly impacts my professional reputation.",
  agency: "Responsibility is split across departments, leading to finger-pointing when campaigns underperform.",
  accentTag: "Personal Ownership",
  icon: ShieldCheck
}];
export default function ConsultantBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="aria-hidden:true pointer-events-none absolute top-0 right-1/4 -z-10 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
      <div className="aria-hidden:true pointer-events-none absolute bottom-10 left-10 -z-10 h-80 w-80 rounded-full bg-secondary/50 blur-2xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-sm font-medium bg-secondary text-secondary-foreground border border-border">
            Why Work Directly With Me
          </Badge>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-serif">
            Independent SEO Consultant vs. Traditional Agency
          </h2>
          
          <p className="text-lg text-muted-foreground leading-relaxed font-sans">
            When you hire YE Consultancy, you don&apos;t get passed off to an account manager. 
            You get direct access to me, Yassine Essaadi, bringing dedicated hands-on expertise to drive your sustainable search growth.
          </p>
        </div>

        {}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }}>
          {comparisonPoints.map((point, index) => {
          const IconComponent = point.icon;
          return <motion.div key={point.id} variants={itemVariants} data-index={index}>
                <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 hover:border-primary/30 rounded-xl overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="size-6" />
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-accent/15 text-accent-foreground border border-accent/20">
                        {point.accentTag}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground font-serif">
                      {point.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 text-sm font-sans flex-1 flex flex-col justify-between">
                    {}
                    <div className="p-3.5 rounded-lg bg-muted/60 border border-border/60">
                      <div className="flex items-start gap-2 mb-1.5">
                        <Check className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="font-medium text-foreground text-xs uppercase tracking-wider">
                          Working With Me
                        </span>
                      </div>
                      <CardDescription className="text-foreground/90 font-medium leading-normal">
                        {point.consultant}
                      </CardDescription>
                    </div>

                    {}
                    <div className="p-3.5 rounded-lg bg-background border border-border/40 text-muted-foreground">
                      <div className="flex items-start gap-2 mb-1.5">
                        <X className="size-4 text-destructive/70 shrink-0 mt-0.5" />
                        <span className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                          Typical Agency
                        </span>
                      </div>
                      <p className="text-muted-foreground text-xs leading-normal">
                        {point.agency}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {}
        <div className="rounded-2xl bg-muted p-8 lg:p-12 border border-border flex flex-col md:flex-row items-center justify-between gap-8 shadow-xs">
          <div className="space-y-3 max-w-2xl text-center md:text-left">
            <h3 className="text-2xl font-semibold text-foreground font-serif">
              Ready for a personal, result-driven SEO partnership?
            </h3>
            <p className="text-muted-foreground text-base font-sans">
              Let&apos;s discuss how a tailored search strategy can increase your organic traffic and lead generation without agency overheads.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent/20">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}